(() => {
  'use strict';

  const body = document.body;
  if (!body || !body.classList.contains('wk-construction-mode')) return;

  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"], a[data-wk-original-href*="/products/"]';
  const PRODUCT_CARD_SELECTOR = 'product-card, .product-card, [data-resource-type="product"]';
  const INTERACTIVE_SELECTOR = 'button, input, select, textarea, label, summary, details, [role="button"]';

  const PRICE_SELECTORS = [
    '.product-price',
    '.price',
    '.money',
    '.compare-at-price',
    '.unit-price',
    '.volume-pricing-note',
    '[class^="price-"]',
    '[class*=" price-"]',
    '[class$="-price"]',
    '[class*="-price "]',
    '[class^="price__"]',
    '[class*=" price__"]',
    '[class$="__price"]',
    '[class*="__price "]',
    '[data-testid*="price"]',
    '[data-product-price]',
    '[data-price]',
    '.bb-price',
    '[data-bb-price-field]',
    '[data-bb-price-hint]',
    '[data-bb-preview-price]',
    '[data-bb-field="priceType"]',
    '[data-bb-field="priceRange"]',
    '.wk-product-copy > span',
    '.gift-card__price'
  ].join(',');

  const LOCKED_UI_SELECTORS = [
    '.nav-account-wrap',
    '.nav-account',
    '#accountDrawer',
    '#accountDrawerOverlay',
    '.account-drawer',
    '.account-drawer-overlay',
    'shopify-account',
    '.nav-cart',
    '.cart-count',
    '#cartDrawer',
    '#cartDrawerOverlay',
    '.cart-drawer',
    '.cart-drawer-overlay',
    'cart-drawer',
    'cart-icon',
    '.cart-icon',
    '.cart-bubble',
    'form[action*="/cart/add"]',
    'product-form',
    '.product-form',
    '.product-form__buttons',
    '.buy-buttons',
    'quick-add-component',
    '.quick-add',
    '.quick-add__button',
    '.sticky-add-to-cart',
    '[data-add-to-cart]',
    '[name="add"]',
    '[name="checkout"]',
    '.shopify-payment-button',
    'shopify-accelerated-checkout',
    '[data-bb-create]',
    '[data-bb-submit-listing]',
    '[data-bb-message-id]',
    '[data-bb-send-message]',
    '[data-bb-review-id]',
    '[data-bb-report-id]',
    '.bb-card-actions',
    '.wk-favorite-button',
    '[data-favorite-id]'
  ].join(',');

  const blockedRoute = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return false;
      return (
        parsed.pathname === '/cart' ||
        parsed.pathname.startsWith('/cart/') ||
        parsed.pathname === '/checkout' ||
        parsed.pathname.startsWith('/checkout/') ||
        parsed.pathname === '/account' ||
        parsed.pathname.startsWith('/account/')
      );
    } catch (_) {
      return false;
    }
  };

  const isProductUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin && parsed.pathname.includes('/products/');
    } catch (_) {
      return false;
    }
  };

  const hideNode = (node) => {
    if (!(node instanceof HTMLElement)) return;
    node.hidden = true;
    node.setAttribute('aria-hidden', 'true');
  };

  const disableProductLink = (link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href') || link.dataset.wkOriginalHref || '';
    if (!isProductUrl(href)) return;

    if (!link.dataset.wkOriginalHref) link.dataset.wkOriginalHref = href;
    link.classList.add('wk-product-preview-only');
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('tabindex', '-1');
  };

  const elementsWithin = (root, selector) => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    if (root && typeof root.querySelectorAll === 'function') elements.push(...root.querySelectorAll(selector));
    return elements;
  };

  const hideUnmarkedCurrencyValues = (root) => {
    const currencyPattern = /(?:€|\bEUR\b|\bCHF\b|\bUSD\b|\bGBP\b|\$)/i;
    const containers = elementsWithin(
      root,
      `${PRODUCT_CARD_SELECTOR}, .wk-product-polaroid, .search-result-item.wk-product-preview-only`
    );

    containers.forEach((container) => {
      container.querySelectorAll('span, p, small, div').forEach((element) => {
        if (element.children.length > 0) return;
        if (currencyPattern.test(element.textContent || '')) hideNode(element);
      });
    });
  };

  const sanitize = (root = document) => {
    elementsWithin(root, 'a[href*="/products/"]').forEach(disableProductLink);
    elementsWithin(root, PRICE_SELECTORS).forEach(hideNode);
    elementsWithin(root, LOCKED_UI_SELECTORS).forEach(hideNode);

    elementsWithin(root, 'a[href^="/account"], a[href*="/account?"], a[href*="/account/"], a[href="/cart"], a[href^="/cart?"], a[href^="/cart/"], a[href="/checkout"], a[href^="/checkout?"]').forEach(hideNode);

    elementsWithin(root, '.search-result-item.wk-product-preview-only div > span').forEach(hideNode);
    hideUnmarkedCurrencyValues(root);
  };

  let toastTimer = 0;
  const showToast = (message) => {
    const toast = document.getElementById('WkPreviewToast');
    if (!toast) return;

    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const blockEvent = (event, message) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    if (message) showToast(message);
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = target.closest('a');
    if (link) {
      const url = link.dataset.wkOriginalHref || link.getAttribute('href') || '';

      if (isProductUrl(url) || link.classList.contains('wk-product-preview-only')) {
        blockEvent(event, 'Die Produkt-Großansicht ist während der Bauphase noch geschlossen.');
        return;
      }

      if (blockedRoute(url)) {
        blockEvent(event, 'Warenkorb und Anmeldung sind in der Vorschau deaktiviert.');
        return;
      }
    }

    const purchaseControl = target.closest(
      '[name="add"], [name="checkout"], [data-add-to-cart], .quick-add__button, .shopify-payment-button, [class*="add-to-cart"]'
    );
    if (purchaseControl) {
      blockEvent(event, 'Der Einkauf ist während der Bauphase noch nicht freigeschaltet.');
      return;
    }

    const blackboardWriteAction = target.closest(
      '[data-bb-create], [data-bb-submit-listing], [data-bb-message-id], [data-bb-send-message], [data-bb-review-id], [data-bb-report-id]'
    );
    if (blackboardWriteAction) {
      blockEvent(event, 'Das Blackboard ist aktuell nur zum Umschauen geöffnet.');
      return;
    }

    const productCard = target.closest(PRODUCT_CARD_SELECTOR);
    if (productCard && !target.closest(INTERACTIVE_SELECTOR)) {
      blockEvent(event, 'Die Produkt-Großansicht ist während der Bauphase noch geschlossen.');
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    const action = form.getAttribute('action') || form.action || '';
    if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(action) || blockedRoute(action)) {
      blockEvent(event, 'Der Einkauf ist während der Bauphase noch nicht freigeschaltet.');
    }
  }, true);

  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (nativeFetch) {
    window.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      let pathname = '';

      try {
        pathname = new URL(url, window.location.origin).pathname;
      } catch (_) {
        pathname = '';
      }

      if (/^\/cart\/(add|change|update|clear)(\.js)?$/.test(pathname) || pathname.startsWith('/checkout')) {
        showToast('Der Einkauf ist während der Bauphase noch nicht freigeschaltet.');
        return Promise.reject(new Error('WK construction mode: cart mutation blocked.'));
      }

      return nativeFetch(input, init);
    };
  }

  const initNoticeSlider = (notice) => {
    const slider = notice.querySelector('[data-wk-notice-slider]');
    if (!slider) return { start() {}, stop() {} };

    const slides = Array.from(slider.querySelectorAll('[data-wk-notice-slide]'));
    const dots = Array.from(slider.querySelectorAll('[data-wk-notice-go]'));
    const previous = slider.querySelector('[data-wk-notice-prev]');
    const next = slider.querySelector('[data-wk-notice-next]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let timer = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let userInteracted = false;

    const show = (nextIndex) => {
      const total = slides.length;
      if (!total) return;
      index = (nextIndex + total) % total;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = 0;
    };

    const start = () => {
      stop();
      if (reduceMotion || userInteracted || slides.length < 2) return;
      timer = window.setInterval(() => show(index + 1), 5200);
    };

    const interact = (nextIndex) => {
      userInteracted = true;
      stop();
      show(nextIndex);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => interact(Number(dot.dataset.wkNoticeGo) || 0));
    });

    if (previous) previous.addEventListener('click', () => interact(index - 1));
    if (next) next.addEventListener('click', () => interact(index + 1));

    slider.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });

    slider.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      interact(deltaX < 0 ? index + 1 : index - 1);
    }, { passive: true });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);

    show(0);
    return { start, stop };
  };

  const showEntryNotice = () => {
    const notice = document.getElementById('WkConstructionNotice');
    if (!notice || body.classList.contains('landing-mode')) return;

    let enteredFromLanding = false;
    let entryTarget = '';

    try {
      enteredFromLanding = sessionStorage.getItem('garageEntered') === 'true';
      entryTarget = sessionStorage.getItem('garageEntryTarget') || '';
      sessionStorage.removeItem('garageEntered');
      sessionStorage.removeItem('garageEntryTarget');
    } catch (_) {
      /* URL markers below remain as a fallback. */
    }

    const currentUrl = new URL(window.location.href);
    const hasPreviewMarker = currentUrl.searchParams.get('wk_preview') === '1';
    const urlEntryTarget = currentUrl.searchParams.get('wk_entry') || '';
    if (!entryTarget && urlEntryTarget) entryTarget = urlEntryTarget;

    if (hasPreviewMarker || urlEntryTarget) {
      currentUrl.searchParams.delete('wk_preview');
      currentUrl.searchParams.delete('wk_entry');
      window.history.replaceState({}, document.title, currentUrl.pathname + currentUrl.search + currentUrl.hash);
    }

    if (!enteredFromLanding && !hasPreviewMarker) return;

    const primaryButton = notice.querySelector('[data-wk-notice-primary]');
    const appButton = notice.querySelector('[data-wk-notice-app]');
    const closeTargets = notice.querySelectorAll('[data-wk-notice-close]');
    const sliderApi = initNoticeSlider(notice);
    let previouslyFocused = document.activeElement;
    let isClosing = false;

    const isBlackboard = entryTarget === 'blackboard' || window.location.pathname.includes('/blackboard');
    if (primaryButton) {
      primaryButton.textContent = isBlackboard ? 'Blackboard ansehen' : 'Shop ansehen';
      primaryButton.setAttribute('aria-label', isBlackboard ? 'Blackboard ansehen und Hinweis schließen' : 'Shop ansehen und Hinweis schließen');
    }

    const close = (restoreFocus = true) => {
      if (isClosing) return;
      isClosing = true;
      sliderApi.stop();
      notice.classList.remove('is-visible');
      notice.setAttribute('aria-hidden', 'true');
      body.classList.remove('wk-notice-open');

      window.setTimeout(() => {
        notice.hidden = true;
        if (restoreFocus && previouslyFocused instanceof HTMLElement) {
          previouslyFocused.focus({ preventScroll: true });
        }
      }, 230);
    };

    closeTargets.forEach((element) => element.addEventListener('click', () => close(true)));

    if (appButton) {
      appButton.addEventListener('click', (event) => {
        event.preventDefault();
        close(false);
        window.setTimeout(() => {
          if (window.WKAppExperience && typeof window.WKAppExperience.open === 'function') {
            window.WKAppExperience.open(appButton, 0);
          }
        }, 245);
      });
    }

    notice.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close(true);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        notice.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    notice.hidden = false;
    notice.setAttribute('aria-hidden', 'false');
    body.classList.add('wk-notice-open');
    sliderApi.start();

    window.requestAnimationFrame(() => {
      notice.classList.add('is-visible');
      if (primaryButton instanceof HTMLElement) primaryButton.focus({ preventScroll: true });
    });
  };

  sanitize(document);
  showEntryNotice();

  let sanitizeScheduled = false;
  const observer = new MutationObserver((mutations) => {
    if (sanitizeScheduled) return;

    const hasAddedNodes = mutations.some((mutation) => mutation.addedNodes && mutation.addedNodes.length);
    if (!hasAddedNodes) return;

    sanitizeScheduled = true;
    window.requestAnimationFrame(() => {
      sanitize(document);
      sanitizeScheduled = false;
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
